// The brand's review queue: every video our team cleared on the brand's
// campaigns, waiting for the brand's yes. The brand watches it beside the
// brief, then approves it (ticking what it liked, optionally marking it a
// favourite with a line for the creator), asks for changes, or finally rejects
// the version with a reason the creator will read. 48 hours per video; after
// that our team chases. Below the queue, what the brand decided in the last 30 days.

import {useMemo, useState} from 'react';
import type {FormEvent} from 'react';
import {Link} from 'react-router-dom';
import type {ApprovalTag} from '../../shared/creator-api';
import type {BrandAppData, BrandCampaign, BrandVideoReview} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {Person} from '../shared/person/Person';
import {BrandApiError, creatorPicture, decideReview, describe} from './api';
import {BriefRead, readBrief} from './brief';
import {useCreatorProfile} from './creator-profile';
import {usePageTitle} from './page-title';
import {useBrand} from './store';

/** The strengths a brand can tick, in the order the creator's Strengths page lists them. */
export const REVIEW_TAGS: {tag: ApprovalTag; label: string}[] = [
  {tag: 'strong_hook', label: 'Strong hook'},
  {tag: 'natural_light', label: 'Natural light'},
  {tag: 'clear_product_shot', label: 'Clear product shot'},
  {tag: 'good_pacing', label: 'Good pacing'},
  {tag: 'on_brief', label: 'On brief'},
  {tag: 'great_energy', label: 'Great energy'},
];

const TAG_LABEL: Partial<Record<ApprovalTag, string>> = {
  ...Object.fromEntries(REVIEW_TAGS.map((entry) => [entry.tag, entry.label])),
  good_audio: 'Good audio',
  authentic: 'Authentic',
};

const REVIEW_HOURS = 48;

/** The videos waiting on this brand, most urgent first. An older Worker sends none. */
export function pendingReviews(data: BrandAppData | null): BrandVideoReview[] {
  return (data?.reviews ?? [])
    .filter((review) => review.status === 'pending')
    .sort((a, b) => (a.due_at ?? '9999').localeCompare(b.due_at ?? '9999'));
}

function fileUrl(review: BrandVideoReview): string {
  return review.file_id ? `/api/brand/files/${review.file_id}` : '';
}

/** The campaign's brief in sections; an older campaign has only free text, which reads as the idea. */
export function campaignBrief(campaign: BrandCampaign) {
  return readBrief((campaign as BrandCampaign & {brief_json?: unknown}).brief_json ?? null, campaign.brief);
}

/** "Due in 31 h", "Due in 40 min", "3 h overdue". */
function dueLabel(dueAt: string | null): {text: string; late: boolean; soon: boolean} {
  if (!dueAt) return {text: `${REVIEW_HOURS} h to review`, late: false, soon: false};
  const minutes = Math.round((Date.parse(dueAt) - Date.now()) / 60_000);
  if (!Number.isFinite(minutes)) return {text: '', late: false, soon: false};
  const size = Math.abs(minutes);
  const span = size < 60 ? `${size} min` : `${Math.round(size / 60)} h`;
  return minutes >= 0 ? {text: `Due in ${span}`, late: false, soon: minutes < 6 * 60} : {text: `${span} overdue`, late: true, soon: false};
}

function shortDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {day: 'numeric', month: 'short'});
}

export function ReviewsPage() {
  const {data} = useBrand();
  usePageTitle('Reviews');
  const pending = useMemo(() => pendingReviews(data), [data]);
  const decided = useMemo(() => (data?.reviews ?? []).filter((review) => review.status !== 'pending'), [data]);
  // The creator's photos, earlier work and accounts, from their name on a card or a row.
  const profile = useCreatorProfile();
  if (!data) return null;
  const late = pending.filter((review) => dueLabel(review.due_at).late).length;
  return (
    <div className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">Reviews</p>
          <h1>Videos to review</h1>
          <p className="lead">Review each video within {REVIEW_HOURS} hours. Approve it, ask for actionable changes, or reject a version you cannot accept.</p>
        </div>
        {pending.length ? <span className={`status-pill due ${late ? 'rejected' : 'in_review'}`}>{pending.length} waiting{late ? ` · ${late} overdue` : ''}</span> : null}
      </header>
      {pending.length === 0 ? (
        <section className="card brand-section">
          <div className="empty-state">
            <Icon name="check" size={28} />
            <b>Nothing waiting for you</b>
            <span>When our team clears a creator's video on one of your campaigns, it lands here for your approval.</span>
            <Link className="btn btn-light" to="/campaigns">See your campaigns<Icon name="arrowRight" size={17} /></Link>
          </div>
        </section>
      ) : (
        <div className="review-list">
          {pending.map((review) => <ReviewCard key={review.deliverable_id} review={review} campaign={data.campaigns.find((item) => item.id === review.project_id) ?? null} onOpen={profile.open} />)}
        </div>
      )}
      {decided.length ? (
        <section className="card brand-section">
          <div className="section-head"><div><h2>Decided in the last 30 days</h2><p>What you told each creator.</p></div></div>
          <div className="decided-list">
            {decided.map((review) => (
              <div className="decided-row" key={review.deliverable_id}>
                <span className={`status-pill ${review.status === 'approved' ? 'active' : review.status === 'rejected' ? 'rejected' : 'in_review'}`}>
                  {review.status === 'approved' ? 'Approved' : review.status === 'rejected' ? 'Rejected' : 'Changes requested'}
                </span>
                <div>
                  <Person size="sm" name={review.creator_name} handle={review.creator_handle} status={review.job_title} sources={creatorPicture(review)} onOpen={() => profile.open(review.account_id)} />
                  {review.status === 'approved' ? (
                    <span className="decided-said">
                      {review.favourite ? <em className="decided-fav"><Icon name="star" size={12} />Favourite</em> : null}
                      {review.tags.map((tag) => TAG_LABEL[tag] ?? tag).join(' · ')}
                      {review.quote ? <q>{review.quote}</q> : null}
                    </span>
                  ) : review.feedback ? <span className="decided-said">{review.feedback}</span> : null}
                </div>
                <small className="muted">{shortDate(review.updated_at)}</small>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {profile.panel}
    </div>
  );
}

function ReviewCard({review, campaign, onOpen}: {review: BrandVideoReview; campaign: BrandCampaign | null; onOpen(accountId: string): void}) {
  const {reload, toast} = useBrand();
  const [mode, setMode] = useState<'idle' | 'approve' | 'changes' | 'reject'>('idle');
  const [tags, setTags] = useState<ApprovalTag[]>([]);
  const [favourite, setFavourite] = useState(false);
  const [quote, setQuote] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const due = dueLabel(review.due_at);
  const url = fileUrl(review);
  const playable = url && (review.file_type.startsWith('video/') || !review.file_type);
  const idBase = `review-${review.deliverable_id}`;

  function toggle(tag: ApprovalTag) {
    setTags((current) => (current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag]));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if ((mode === 'changes' || mode === 'reject') && feedback.trim().length < 4) {
      setError(mode === 'reject' ? 'Say why this video cannot be accepted.' : 'Say what should change, so the creator can fix it.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'approve') {
        await decideReview(review.deliverable_id, {decision: 'approve', tags, favourite, quote: favourite ? quote.trim() : ''});
        // A made-to-hand-over video is not posted by the creator; only the others go up next.
        const next = campaign?.delivery_mode === 'file_only' ? `${review.creator_name} will hear it.` : `${review.creator_name} can post it now.`;
        toast(favourite ? `Approved and marked as a favourite. ${review.creator_name} will hear it.` : `Approved. ${next}`);
      } else if (mode === 'changes') {
        await decideReview(review.deliverable_id, {decision: 'changes', feedback: feedback.trim()});
        toast(`Sent to ${review.creator_name}. They will upload a new version.`);
      } else if (mode === 'reject') {
        await decideReview(review.deliverable_id, {decision: 'reject', feedback: feedback.trim()});
        toast(`Rejected. ${review.creator_name} can submit a new video for the campaign.`);
      }
      await reload(true);
    } catch (caught) {
      const fields = caught instanceof BrandApiError ? caught.fields : {};
      if (fields.feedback || fields.tags || fields.decision) setError(fields.feedback || fields.tags || fields.decision || '');
      else {
        toast(describe(caught), true);
        // Decided elsewhere or taken back by the team: show the list as it is now.
        if (caught instanceof BrandApiError && caught.status === 409) await reload(true);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="card video-review" aria-labelledby={`${idBase}-title`}>
      <div className="review-video">
        {playable ? (
          <video src={`${url}#t=0.1`} controls playsInline preload="metadata" aria-label={`Video by ${review.creator_name}`} />
        ) : (
          <div className="review-file"><Icon name="file" size={28} /><span>{review.file_name || 'The file is not available yet.'}</span></div>
        )}
        {url ? (
          <div className="review-file-links">
            <a href={url} target="_blank" rel="noopener"><Icon name="external" size={14} />Open</a>
            <a href={`${url}?download=1`} download={review.file_name}><Icon name="download" size={14} />Download</a>
          </div>
        ) : null}
      </div>
      <div className="review-body">
        <header className="review-head">
          <div>
            <p className="eyebrow">{review.campaign_title}{review.job_title && review.job_title !== review.campaign_title ? ` · ${review.job_title}` : ''}</p>
            <h2 id={`${idBase}-title`} className="sr-only">{review.creator_name}</h2>
            <Person size="lg" name={review.creator_name} handle={review.creator_handle} status={`Handed in ${shortDate(review.handed_in_at)}`}
              sources={creatorPicture(review)} onOpen={() => onOpen(review.account_id)} />
          </div>
          {due.text ? <span className={`status-pill due${due.late ? ' rejected' : due.soon ? ' in_review' : ''}`}><Icon name="clock" size={13} />{due.text}</span> : null}
        </header>
        {review.note ? <p className="review-note"><b>The creator wrote</b>{review.note}</p> : null}
        {campaign ? (
          <details className="review-brief" open>
            <summary>Your brief</summary>
            <BriefRead brief={campaignBrief(campaign)} compact campaignId={campaign.id} />
          </details>
        ) : null}

        {mode === 'idle' ? (
          <div className="review-actions">
            <button className="btn btn-g" type="button" onClick={() => setMode('approve')}><Icon name="check" size={17} />Approve</button>
            <button className="btn btn-light" type="button" onClick={() => setMode('changes')}><Icon name="edit" size={16} />Request changes</button>
            <button className="btn btn-danger" type="button" onClick={() => setMode('reject')}><Icon name="x" size={16} />Reject</button>
          </div>
        ) : (
          <form className="review-decision" onSubmit={(event) => void submit(event)}>
            {mode === 'approve' ? (
              <>
                <fieldset className="brand-field">
                  <legend><span>What worked?</span> <small>Optional. The creator sees these as their strengths.</small></legend>
                  <div className="chip-row">
                    {REVIEW_TAGS.map((entry) => (
                      <button key={entry.tag} type="button" className={`choice-chip${tags.includes(entry.tag) ? ' active' : ''}`} aria-pressed={tags.includes(entry.tag)} onClick={() => toggle(entry.tag)}>
                        {tags.includes(entry.tag) ? <Icon name="check" size={12} /> : null}{entry.label}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <label className="check-row favourite-row">
                  <input type="checkbox" checked={favourite} onChange={(event) => setFavourite(event.target.checked)} />
                  <span><b>Brand favourite</b> The creator gets a moment and a badge that says you loved it.</span>
                </label>
                {favourite ? (
                  <label className="brand-field">
                    <span>A line for the creator <small>Optional</small></span>
                    <textarea rows={2} maxLength={280} value={quote} placeholder="The hook is perfect. We're using it in our ads." onChange={(event) => setQuote(event.target.value)} />
                  </label>
                ) : null}
              </>
            ) : mode === 'changes' ? (
              <label className={`brand-field${error ? ' field-error' : ''}`}>
                <span>What should change?</span>
                <textarea rows={4} maxLength={1000} value={feedback} required aria-invalid={Boolean(error) || undefined} placeholder="Show the can in the first two seconds, and end on it opening." onChange={(event) => setFeedback(event.target.value)} autoFocus />
                <small>The creator reads this word for word and uploads a new version. Keep it concrete.</small>
              </label>
            ) : (
              <label className={`brand-field${error ? ' field-error' : ''}`}>
                <span>Why are you rejecting this video?</span>
                <textarea rows={4} maxLength={1000} value={feedback} required aria-invalid={Boolean(error) || undefined} placeholder="Explain clearly why this version cannot be accepted." onChange={(event) => setFeedback(event.target.value)} autoFocus />
                <small>This is final for this version: it earns no fee. The creator will see your reason and can submit a new video.</small>
              </label>
            )}
            {error ? <p className="form-alert" role="alert">{error}</p> : null}
            <div className="review-actions">
              <button className={`btn ${mode === 'reject' ? 'btn-danger' : 'btn-g'}`} type="submit" disabled={busy}>
                {busy ? 'Sending…' : mode === 'approve' ? 'Approve video' : mode === 'reject' ? 'Reject video' : 'Send to the creator'}
                {busy ? null : <Icon name={mode === 'approve' ? 'check' : mode === 'reject' ? 'x' : 'send'} size={16} />}
              </button>
              <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => { setMode('idle'); setError(''); }}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </article>
  );
}
